import DataArrayRoundedIcon from '@mui/icons-material/DataArrayRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import WidgetsRoundedIcon from '@mui/icons-material/WidgetsRounded';
import PaletteRoundedIcon from '@mui/icons-material/PaletteRounded';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import Tab, { tabClasses } from '@mui/material/Tab';
import Tabs, { tabsClasses } from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { COLORS } from 'muiTheme/colors';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RenderEditorSidePanelSettings } from '../RenderEditorSidePanelSettings';
import { ContentType } from 'utils/getContentType';
import { SidePanelModels, SidePanelProviderContext } from '../content';
import { useAppDispatch, useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectContentId, selectContentType } from 'components/editor/grid/reduxStore/documentSettingsSlice';
import { TableSettingsTypes } from '../content/TableSettings/types';
import {
  EditorDefaultSidePanelType,
  selectIsRightPanelCollapsed,
  setActiveSidePanel,
  setIsAdvancedSpacingModelOpen,
  setIsReplaceModeActive,
  setIsRightPanelCollapsed,
} from 'components/editor/grid/reduxStore/blockStyleSettingsSlice';
import { GridBlockType } from 'services/entities/BlockTypes';
import { EditorToolbar } from '../../toolbar';
import { ImagesUploadsPanel } from '../content/LibrarySettings/ImagesUploadsPanel';
import { Variables } from '../Variables';
import { Signatures } from '../Signatures';
import { useComments } from 'providers/CommentsContext';
import { DesignSettingsPanel } from '../content/DesignSettings/DesignSettingsPanel';
import AIAssistantWrapper from '../AIAssitant/AIAssitantWrapper';
import { FeatureFlags } from 'utils/featureFlags';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { TOP_BAR_HEIGHT } from 'components/editor/DocumentBuilder/DocumentBuilderLayout';
import { muiEditorSidePanelTestIds } from './MuiEditorSidePanel.test-ids';

type SidePanelTabConfig = {
  id: 'document_tab' | 'build_tab' | 'uploads_tab' | 'design_tab' | 'signatures_tab' | 'variables_tab' | 'ai_tab';
  label: string;
  Icon: React.ComponentType<SvgIconProps>;
  children?: React.ReactNode;
  testId: string;
};

const tabsByContentType: Record<ContentType, Array<SidePanelTabConfig['id']>> = {
  [ContentType.DOCUMENT]: ['document_tab', 'build_tab', 'uploads_tab', 'design_tab', 'signatures_tab', 'variables_tab', 'ai_tab'],
  [ContentType.TEMPLATE]: ['document_tab', 'build_tab', 'uploads_tab', 'design_tab', 'signatures_tab', 'variables_tab', 'ai_tab'],
  [ContentType.SECTION]: ['build_tab', 'uploads_tab', 'design_tab'],
};

export function MuiEditorSidePanel({ setIsAiTabActive }: { setIsAiTabActive: (isAiTabActive: boolean) => void }) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const contentType = useAppSelector(selectContentType);
  const contentId = useAppSelector(selectContentId);
  const { [FeatureFlags.aiChatSidePannel]: isAiChatEnabled } = useFeatureFlag([FeatureFlags.aiChatSidePannel]);
  const [activeTab, setActiveTab] = useState(contentType === ContentType.SECTION ? 'build_tab' : 'document_tab');
  const { activeSidePanel } = useAppSelector((state) => state.blockStyleSettings);
  const { setToggledTableSettingsPanel } = useContext(SidePanelProviderContext);
  const { commentsEnabled, toggleComments } = useComments();
  const isRightPanelCollapsed = useAppSelector(selectIsRightPanelCollapsed);

  const isEditorToolbarDisplayed =
    TableSettingsTypes[activeSidePanel] ||
    GridBlockType[activeSidePanel] ||
    activeSidePanel === EditorDefaultSidePanelType.DEFAULT_STYLE_SETTINGS_PANEL;

  const isSnippetsPanelOpened = activeSidePanel === EditorDefaultSidePanelType.TEXT_SNIPPET_LIBRARY_PANEL;
  const isUploadsPanelOpened = activeSidePanel === EditorDefaultSidePanelType.UPLOADS_LIBRARY_PANEL;
  const isFormatSettingsPanelOpened = activeSidePanel === EditorDefaultSidePanelType.FORMAT_SETTINGS_PANEL;

  const handleTabChange = (key: string) => {
    dispatch(setIsRightPanelCollapsed(false));
    let panelToOpen = EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL;

    if (key === 'uploads_tab') {
      panelToOpen = EditorDefaultSidePanelType.UPLOADS_LIBRARY_PANEL;
    }

    if (key !== 'build_tab') {
      setToggledTableSettingsPanel(null);
      dispatch(setIsAdvancedSpacingModelOpen(false));
    }

    dispatch(setActiveSidePanel({ type: panelToOpen }));
    setActiveTab(key);
    setIsAiTabActive(key === 'ai_tab');

    if (commentsEnabled) toggleComments();
  };

  useEffect(() => {
    if (isEditorToolbarDisplayed || isSnippetsPanelOpened) {
      setActiveTab('build_tab');
      setIsAiTabActive(false);
    } else if (isUploadsPanelOpened) {
      setActiveTab('uploads_tab');
      setIsAiTabActive(false);
    } else if (isFormatSettingsPanelOpened) {
      setActiveTab('design_tab');
      setIsAiTabActive(false);
    }
  }, [activeSidePanel]);

  // Comments can enable independently of activeSidePanel/isRightPanelCollapsed (e.g. a
  // comments deep-link resolving via URL params), which hides activeTabChildren the same
  // way collapsing the panel does. Reset replace mode here too, so it can't leak into a
  // later reopen of the uploads panel.
  useEffect(() => {
    if (commentsEnabled) {
      dispatch(setIsReplaceModeActive(false));
    }
  }, [commentsEnabled, dispatch]);

  const tabs = useMemo(() => {
    return (
      [
        {
          id: 'document_tab',
          label: t('editor.nav_tab.details'),
          Icon: DescriptionRoundedIcon,
          children: <RenderEditorSidePanelSettings contentId={contentId} />,
          testId: muiEditorSidePanelTestIds.documentTab,
        },
        {
          id: 'build_tab',
          label: t('editor.nav_tab.build'),
          Icon: WidgetsRoundedIcon,
          children: (
            <>
              <SidePanelModels />
              {activeSidePanel === EditorDefaultSidePanelType.EDITOR_DEFAULT_SIDE_PANEL && <EditorToolbar />}
            </>
          ),
          testId: muiEditorSidePanelTestIds.buildTab,
        },
        {
          id: 'uploads_tab',
          label: t('editor.nav_tab.uploads'),
          Icon: ImageRoundedIcon,
          children: <ImagesUploadsPanel />,
          testId: muiEditorSidePanelTestIds.uploadsTab,
        },
        {
          id: 'design_tab',
          label: t('editor.nav_tab.design'),
          Icon: PaletteRoundedIcon,
          children: <DesignSettingsPanel />,
          testId: muiEditorSidePanelTestIds.designTab,
        },

        {
          id: 'signatures_tab',
          label: t('editor.nav_tab.signatures'),
          Icon: HistoryEduRoundedIcon,
          children: <Signatures />,
          testId: muiEditorSidePanelTestIds.signaturesTab,
        },
        {
          id: 'variables_tab',
          label: t('editor.nav_tab.variables'),
          Icon: DataArrayRoundedIcon,
          children: <Variables />,
          testId: muiEditorSidePanelTestIds.variablesTab,
        },
        {
          id: 'ai_tab',
          label: t('editor.nav_tab.ai'),
          Icon: AutoAwesomeIcon,
          children: <AIAssistantWrapper contentId={contentId} />,
          testId: muiEditorSidePanelTestIds.aiTab,
        },
      ] satisfies SidePanelTabConfig[]
    ).filter((t) => {
      if (!isAiChatEnabled && t.id == 'ai_tab') return false;
      else return tabsByContentType[contentType].includes(t.id);
    });
  }, [activeSidePanel, contentType, contentId, activeTab, t, isAiChatEnabled]);

  const activeTabData = tabs.find((tab) => tab.id === activeTab);
  const activeTabChildren = activeTabData?.children;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) 5rem',
        gridTemplateRows: '1fr',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: 'background.paper',
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          minWidth: 0,
          padding: isRightPanelCollapsed ? 'hidden' : activeTab === 'ai_tab' ? 0 : 2,
          overflow: isRightPanelCollapsed ? 0 : activeTab === 'ai_tab' ? 'hidden' : 'auto',
          maxHeight: `calc(100vh - ${TOP_BAR_HEIGHT})`,
          minHeight: 0,
          position: 'relative',
          height: '100%',
          alignSelf: 'stretch',
        }}
        data-testid={muiEditorSidePanelTestIds.content}
      >
        {!commentsEnabled && !isRightPanelCollapsed && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Typography variant="h6">{activeTabData?.label}</Typography>
              <IconButton size="small" onClick={() => dispatch(setIsRightPanelCollapsed(true))} aria-label="Collapse panel">
                <KeyboardDoubleArrowRightRoundedIcon />
              </IconButton>
            </Box>
            {activeTabChildren}
          </>
        )}
      </Box>

      <Box sx={{ borderLeft: `1px solid ${COLORS.sidePanelTabBorder}` }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => handleTabChange(newValue)}
          orientation="vertical"
          textColor="primary"
          TabIndicatorProps={{
            sx: { display: 'none' },
          }}
          sx={{
            py: 1,
            px: 0.5,
            [`& .${tabsClasses.flexContainer}`]: {
              gap: 1,
            },
          }}
        >
          {tabs.map(({ id, label, Icon, testId }) => {
            const isSelected = activeTab === id;
            return (
              <Tab
                disableRipple
                key={id}
                id={id}
                value={id}
                data-node-key={id}
                onClick={() => {
                  if (activeTab === id) handleTabChange(id);
                }}
                label={
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: 'none',
                      color: 'text.primary',
                      letterSpacing: '0.45px',
                    }}
                  >
                    {label}
                  </Typography>
                }
                icon={
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      p: 1,
                      borderRadius: 2,
                      backgroundColor: isSelected ? COLORS.sidePanelTabSelectedBg : 'transparent',
                      '& .MuiSvgIcon-root': {
                        color: isSelected ? 'secondary.main' : 'action.active',
                      },
                    }}
                  >
                    <Icon />
                  </Box>
                }
                sx={{
                  width: '100%',
                  minWidth: 0,
                  minHeight: 60,
                  textTransform: 'none',
                  padding: 0,
                  [`& .${tabClasses.iconWrapper}`]: {
                    marginBottom: 0,
                  },
                  [`&:hover .${tabClasses.iconWrapper}`]: {
                    backgroundColor: isSelected ? COLORS.sidePanelTabSelectedBg : COLORS.sidePanelTabHoverBg,
                  },
                  '&&.Mui-focusVisible, &&:focus-visible': {
                    backgroundColor: 'transparent',
                    boxShadow: `inset 0 0 0 2px ${COLORS.sidePanelTabFocus}`,
                    borderRadius: 1,
                  },
                }}
                data-testid={testId}
              />
            );
          })}
        </Tabs>
      </Box>
    </Box>
  );
}
